import TurnLeftIcon from "@mui/icons-material/TurnLeft";
import { Box, Stack } from "@mui/material";
import React from "react";

import { Typography } from "new-components/typography/Typography";
import { TierType } from "types/users";

import { AccountBillingInfo } from "./servicePlanUtils";

interface ServicePlanDataLabelProps {
  text: string | React.ReactNode;
  limitNumber?: number | null;
  currentNumber?: number | null;
}

export function ServicePlanDataLabel(props: ServicePlanDataLabelProps) {
  const { text, limitNumber, currentNumber } = props;
  return (
    <Typography
      sx={{
        fontSize: "14px",
        lineHeight: "24px",
        color: currentNumber && limitNumber && currentNumber >= limitNumber ? "#FF0000" : "#49494D",
      }}
    >
      {text}
    </Typography>
  );
}

export interface DescriptionItemProps {
  children?: React.ReactNode;
  title: string;
  limitNumber?: number | null;
  currentNumber?: number | null;
  type?: "detector" | "other";
}

export function DescriptionItem(props: DescriptionItemProps) {
  const { children, title, limitNumber, currentNumber, type = "other" } = props;

  return (
    <Box sx={{ display: "flex", gap: "16px" }}>
      <TurnLeftIcon sx={{ transform: "rotate(180deg)", color: "#00000026" }} />

      <Stack sx={{ gap: "1px" }}>
        <ServicePlanDataLabel
          text={`${title}: ${limitNumber === null ? "Unlimited" : limitNumber}${type === "detector" ? ` (Currently Active: ${currentNumber})` : ""
            }`}
        />
        {currentNumber !== undefined && type !== "detector" ? (
          <ServicePlanDataLabel
            text={`${currentNumber} used this month`}
            limitNumber={limitNumber}
            currentNumber={currentNumber}
          />
        ) : null}
        {children && children}
      </Stack>
    </Box>
  );
}

export interface DescriptionPlanSectionProps {
  planType: TierType;
  accountBillingInfo: AccountBillingInfo;
}

export function DescriptionPlanSection(props: DescriptionPlanSectionProps) {
  const { accountBillingInfo, planType } = props;

  const isFree = planType === TierType.Free;

  return (
    <>
      <DescriptionItem
        title="Included Queries"
        currentNumber={isFree ? accountBillingInfo.iqs : undefined}
        limitNumber={accountBillingInfo.iqs_limit}
      >
        {isFree ? undefined : (
          <>
            <ServicePlanDataLabel text="Overage Queries: 0" />
            <ServicePlanDataLabel
              text={
                <>
                  <b>Total Queries:{" "}
                    {accountBillingInfo.iqs_limit === null
                      ? "Unlimited"
                      : accountBillingInfo.iqs_limit
                    }{" "}
                  </b>
                  {" "}
                  ({accountBillingInfo.iqs} used this month)
                </>
              }
            />
          </>
        )}
      </DescriptionItem>

      <DescriptionItem
        title="Included Groundlight cloud labelers"
        currentNumber={isFree ? accountBillingInfo.escalations : undefined}
        limitNumber={accountBillingInfo.escalations_limit}
      >
        {isFree ? undefined : (
          <>
            <ServicePlanDataLabel text="Overage Groundlight cloud labelers: 0" />
            <ServicePlanDataLabel
              text={
                <>
                  <b>
                    Total Groundlight cloud labelers:{" "}
                    {accountBillingInfo.escalations_limit === null
                      ? "Unlimited"
                      : accountBillingInfo.escalations_limit
                    }{" "}
                  </b>
                  {" "}
                  ({accountBillingInfo.escalations || 0} used this month)
                </>
              }
            />
          </>
        )}
      </DescriptionItem>

      <DescriptionItem
        title="Active Detector Limit"
        currentNumber={accountBillingInfo.active_detectors}
        limitNumber={accountBillingInfo.active_detectors_limit}
        type="detector"
      />
    </>
  );
}
